import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogContent,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { filter, isEmpty, map, size, some, sortBy, toLower, uniq } from 'es-toolkit/compat';
import React from 'react';

import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import { FileToDownload } from 'components/atoms/DownloadFilesModal';
import Loader from 'components/Loader';
import { ExportErrorAlert } from 'components/ResultsExport/ExportErrorAlert';
import { ExportScopeField, ExportScopeFields, ExportScopeValue } from 'components/ResultsExport/ExportFormFields';
import { ExportFormShell } from 'components/ResultsExport/ExportFormShell';
import { getScopeOptionLabels } from 'components/ResultsExport/exportScopeLabels';
import { useResultsExportContext } from 'components/ResultsExport/useResultsExportContext';
import { toWorkflowResultsExportContext } from 'components/ResultsExport/workflowResultsExportContext';
import { ResultsMode } from 'interfaces/experimentResults';
import { Study } from 'interfaces/study';
import { buildExportErrorReport, describeExportFailure } from 'services/export/exportErrorReport';
import {
  FormattedCellTableEntry,
  FormattedCellTableError,
  prepareFormattedCellTable,
} from 'services/export/formattedCellTables';
import { StudyExportOperation, useStudyExportController } from 'services/export/useStudyExportController';
import { CellTableRunDiscoveryFailure } from 'services/export/workflowCellTableDiscovery';
import {
  createCellTableRunCache,
  getCachedCellTableRuns,
  hasUncachedRuns,
  loadCellTableRuns,
  refreshCellTableEntryLinks,
  CellTableRunsLoadProgress,
} from 'services/export/workflowCellTableRuns';
import { useWorkflowResultsSource } from './useWorkflowResultsSource';
import { WorkflowResultsSourceSelect } from './WorkflowResultsSourceSelect';
import { downloadBlob } from 'utils/exports';
import { formatBytes } from 'utils/helpers';
import { downloadFilesAsZip, getZipEntryFileNames, ZipDownloadError } from 'utils/zipDownload';

const VISIBLE_FILES_STEP = 50;
const noop = (): void => undefined;

// The column renaming failed on a readable file, so its stored original can still be offered. An expired link is not
// a renaming failure.
const isRenameFailure = (error: unknown): error is FormattedCellTableError =>
  error instanceof FormattedCellTableError && Boolean(error.code) && error.code !== 'source_unavailable';

const getOriginalFileName = (file: FormattedCellTableEntry['file']) => {
  const [name] = getZipEntryFileNames([{ ...file, url: file.urlForGetRequest }]);
  return `${name.replace(/\.parquet$/, '')}_original.parquet`;
};

const downloadOriginalFile = async (file: FormattedCellTableEntry['file'], signal: AbortSignal) => {
  const response = await fetch(file.urlForGetRequest, { signal });
  if (!response.ok) throw new Error(`HTTP ${response.status}: the original file could not be retrieved.`);
  downloadBlob(await response.blob(), getOriginalFileName(file));
};

interface WorkflowCellTablesExportDialogProps {
  study?: Study;
  onClose: () => void;
}

type LoadState =
  | { phase: 'loading'; message: string }
  | { phase: 'failed'; errors: string[] }
  | {
      phase: 'ready';
      entries: FormattedCellTableEntry[];
      // Runs that could not be read or have no final cell tables; the other runs stay downloadable.
      failures: CellTableRunDiscoveryFailure[];
      emptyRunIds: string[];
      // Files that shared a URL with another listed file and are listed only once.
      collapsedDuplicates: number;
      // Set while runs that were added are still being read; the tables read so far stay listed.
      loadingMessage?: string;
    };

interface CellTableGroup {
  key: string;
  // `unavailable` tables cannot be formatted for a stated reason; `error` tables could not be read because something
  // failed, which is not a property of the table.
  kind: 'tables' | 'unavailable' | 'error';
  // Column names of the downloaded files; empty for tables that cannot be exported.
  columns: string[];
  entries: FormattedCellTableEntry[];
}

const getDownloadedColumns = (entry: FormattedCellTableEntry) =>
  entry.mapping ? map(entry.mapping.columns, (column) => column.outputName ?? column.rawName) : entry.columnNames;

// Exportable tables with the same set of columns share a group; tables that cannot be exported, then tables that
// could not be read because of an error, come last.
const groupByColumns = (entries: FormattedCellTableEntry[]): CellTableGroup[] => {
  const groups = new Map<string, CellTableGroup>();
  const unavailable: CellTableGroup = { key: 'unavailable', kind: 'unavailable', columns: [], entries: [] };
  const failed: CellTableGroup = { key: 'error', kind: 'error', columns: [], entries: [] };
  entries.forEach((entry) => {
    if (entry.status === 'error') {
      failed.entries.push(entry);
      return;
    }
    if (entry.status !== 'ready') {
      unavailable.entries.push(entry);
      return;
    }
    const columns = getDownloadedColumns(entry);
    const key = JSON.stringify(sortBy(columns));
    const group = groups.get(key) ?? { key, kind: 'tables', columns, entries: [] };
    group.entries.push(entry);
    groups.set(key, group);
  });
  return [
    ...groups.values(),
    ...(unavailable.entries.length ? [unavailable] : []),
    ...(failed.entries.length ? [failed] : []),
  ];
};

const describeRunFailure = ({ runId, message }: CellTableRunDiscoveryFailure) =>
  runId ? `Run ${runId}: ${message}` : message;

const countLabel = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const describeFailedRuns = (count: number) =>
  count === 1
    ? '1 run could not be read, so its tables are not listed'
    : `${count} runs could not be read, so their tables are not listed`;

const CellTableGroupHeader: React.FC<{ group: CellTableGroup; tableCount: number; groupIndex: number }> = ({
  group,
  tableCount,
  groupIndex,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const panelId = `workflow-cell-table-columns-${groupIndex}`;
  const hasColumns = group.kind === 'tables';
  return (
    <Box sx={{ mt: groupIndex === 0 ? 0 : 1.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle2" component="h4">
          {group.kind === 'error'
            ? `${countLabel(tableCount, 'table')} that could not be read because of an error`
            : group.kind === 'unavailable'
            ? `${countLabel(tableCount, 'table')} that cannot be exported`
            : `${countLabel(tableCount, 'table')}, ${countLabel(group.columns.length, 'column')}${
                tableCount > 1 ? ' each' : ''
              }`}
        </Typography>
        {hasColumns && (
          <Button
            size="small"
            aria-expanded={expanded}
            aria-controls={panelId}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? 'Hide columns' : 'Show columns'}
          </Button>
        )}
      </Stack>
      {hasColumns && (
        <Collapse in={expanded} unmountOnExit id={panelId}>
          <Stack
            component="ul"
            spacing={0.25}
            aria-label="Columns"
            sx={{ m: 0, mt: 0.5, pl: 2, maxHeight: 200, overflowY: 'auto' }}
          >
            {map(group.columns, (column, index) => (
              <Typography key={`${column}-${index}`} component="li" variant="caption" color="text.secondary">
                {column}
              </Typography>
            ))}
          </Stack>
        </Collapse>
      )}
    </Box>
  );
};

// Opened from Review and publication. Final cell tables for the whole study, downloaded as Parquet with the
// study's panel display names in the column names; the stored files are never changed.
export const WorkflowCellTablesExportDialog: React.FC<WorkflowCellTablesExportDialogProps> = ({ study, onClose }) => {
  const baseContext = useResultsExportContext(study);
  const source = useWorkflowResultsSource('cellTables', study);
  const { resultsMode, orchestrationIds } = source.modeParams;
  const runKey = orchestrationIds.join(',');
  // With several runs the context carries the first; discovery reads each run in `orchestrationIds` on its own.
  const context = React.useMemo(
    () => toWorkflowResultsExportContext(baseContext, { resultsMode, orchestrationId: orchestrationIds[0] }),
    [baseContext, resultsMode, runKey]
  );
  const { startExport, exportProgressDialog, isExportRunning } = useStudyExportController();
  const availability = baseContext.canDownloadCellsTable
    ? source.availability
    : { disabled: true, note: 'You do not have permission to download cell tables.' };
  // A source that still needs a run to be chosen has nothing to search yet.
  const needsRun = source.selection.source === 'run' && isEmpty(source.selection.runIds);
  const { clear: clearSource } = source;
  const close = () => {
    clearSource();
    onClose();
  };

  const [loadState, setLoadState] = React.useState<LoadState>({
    phase: 'loading',
    message: 'Finding the final cell tables…',
  });
  // Null means no choice has been made yet, so every exportable table is selected.
  const [selectedIds, setSelectedIds] = React.useState<Set<string> | null>(null);
  const [search, setSearch] = React.useState('');
  const [visibleCount, setVisibleCount] = React.useState(VISIBLE_FILES_STEP);
  const [handoffMessage, setHandoffMessage] = React.useState('');

  const { studyId } = context;
  const hasSeveralRuns = size(orchestrationIds) > 1;
  const isAvailable = !availability.disabled && !needsRun;
  // Without the unpublished-results permission there is no source to choose, a study that does not allow
  // export has nothing to choose either, and neither has one without any results, so an unavailable export ends here.
  const isUnavailableForUser =
    availability.disabled &&
    (!source.canViewUnpublishedResults ||
      !study?.allowExport ||
      !baseContext.canDownloadCellsTable ||
      source.hasNoResults);

  // What has been read for each run stays for as long as the dialog is open, so changing the runs reads only the
  // runs that were added.
  const runCache = React.useRef(createCellTableRunCache());

  React.useEffect(() => {
    if (!isAvailable || !studyId) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    const scopes = map(orchestrationIds.length ? orchestrationIds : [undefined], (runId) => ({
      key: `${studyId}|${resultsMode}|${runId ?? ''}`,
      runId,
      casesParams: toWorkflowResultsExportContext(baseContext, { resultsMode, orchestrationId: runId })
        .casesParamsWithoutPaginationAndSorting,
    }));
    const seenReadyIds = new Set(
      map(
        filter(getCachedCellTableRuns(runCache.current, scopes).entries, { status: 'ready' }),
        (entry) => entry.file.id
      )
    );
    const publish = ({
      entries: loadedEntries,
      failures,
      emptyRunIds,
      collapsedDuplicates,
      pendingFiles,
      searchingRuns,
    }: CellTableRunsLoadProgress) => {
      if (signal.aborted) return;
      const listedIds = new Set(map(filter(loadedEntries, { status: 'ready' }), (entry) => entry.file.id));
      const newIds = filter([...listedIds], (id) => !seenReadyIds.has(id));
      listedIds.forEach((id) => seenReadyIds.add(id));
      setSelectedIds((previous) =>
        previous ? new Set([...filter([...previous], (id) => listedIds.has(id)), ...newIds]) : previous
      );
      const loadingMessage =
        pendingFiles || searchingRuns
          ? `${searchingRuns ? 'Finding the final cell tables…' : 'Resolving cell tables…'} ${countLabel(
              listedIds.size,
              'cell table'
            )} ready to download${
              pendingFiles ? `; ${countLabel(pendingFiles, 'cell table')} still being resolved` : ''
            }${
              searchingRuns
                ? `; ${countLabel(searchingRuns, orchestrationIds.length ? 'run' : 'source')} still being searched`
                : ''
            }.`
          : undefined;
      if (!loadingMessage && failures.length === scopes.length && isEmpty(loadedEntries)) {
        setLoadState({ phase: 'failed', errors: map(failures, describeRunFailure) });
      } else if (loadingMessage && isEmpty(loadedEntries)) {
        setLoadState({ phase: 'loading', message: loadingMessage });
      } else {
        setLoadState({
          phase: 'ready',
          entries: loadedEntries,
          failures,
          emptyRunIds,
          collapsedDuplicates,
          loadingMessage,
        });
      }
    };
    if (hasUncachedRuns(runCache.current, scopes)) {
      const message = 'Finding the final cell tables…';
      const {
        hasCachedRun,
        entries: cachedEntries,
        emptyRunIds,
        collapsedDuplicates,
      } = getCachedCellTableRuns(runCache.current, scopes);
      setLoadState(
        hasCachedRun
          ? {
              phase: 'ready',
              entries: cachedEntries,
              failures: [],
              emptyRunIds,
              collapsedDuplicates,
              loadingMessage: message,
            }
          : { phase: 'loading', message }
      );
    }
    setHandoffMessage('');
    (async () => {
      try {
        const inventory = await loadCellTableRuns({
          studyId,
          scopes,
          cache: runCache.current,
          signal,
          onUpdate: publish,
        });
        publish({ ...inventory, pendingFiles: 0, searchingRuns: 0 });
      } catch (error) {
        if (!signal.aborted) {
          setLoadState({
            phase: 'failed',
            errors: [describeExportFailure(error, 'The cell tables could not be loaded.')],
          });
        }
      }
    })();
    return () => controller.abort();
    // The inventory belongs to the study, result source and orchestration the dialog opened with.
  }, [isAvailable, studyId, resultsMode, runKey]);

  const entries = loadState.phase === 'ready' ? loadState.entries : [];
  const exportableEntries = filter(entries, { status: 'ready' });
  const unreadableEntries = filter(entries, { status: 'error' });
  const unavailableCount = size(entries) - size(exportableEntries) - size(unreadableEntries);
  const sourceLabel =
    resultsMode === ResultsMode.Manual
      ? `Post-processing runs: ${orchestrationIds.join(', ')}`
      : resultsMode === ResultsMode.InternallyApproved
      ? 'Approved results'
      : 'Published results';
  const reportFor = (errors: string[]) =>
    buildExportErrorReport({
      operation: 'Export cell tables (formatted Parquet)',
      studyId,
      labId: context.labId,
      source: sourceLabel,
      errors,
    });
  const effectiveSelection = selectedIds ?? new Set(map(exportableEntries, (entry) => entry.file.id));
  const selectedEntries = filter(exportableEntries, (entry) => effectiveSelection.has(entry.file.id));

  const searchTerm = toLower(search.trim());
  const matchingEntries = searchTerm
    ? filter(entries, (entry) =>
        toLower(`${entry.file.title} ${entry.file.slideId} ${entry.file.orchestrationId ?? ''}`).includes(searchTerm)
      )
    : entries;
  // Page by table, not by group, so a group is cut where the page ends and continues under "Show more".
  const visibleGroups = (() => {
    let remaining = visibleCount;
    return filter(
      map(groupByColumns(matchingEntries), (group) => {
        const shown = {
          ...group,
          tableCount: group.entries.length,
          entries: group.entries.slice(0, Math.max(remaining, 0)),
        };
        remaining -= group.entries.length;
        return shown;
      }),
      (group) => !isEmpty(group.entries)
    );
  })();

  const toggle = (id: string) => {
    const next = new Set(effectiveSelection);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const guidance = !isAvailable
    ? availability.note ?? ''
    : loadState.phase === 'loading'
    ? loadState.message
    : loadState.phase === 'failed'
    ? 'An error stopped the cell tables from loading.'
    : loadState.loadingMessage && isEmpty(selectedEntries)
    ? loadState.loadingMessage
    : isEmpty(entries)
    ? 'There are no final cell tables for this study and result source.'
    : isEmpty(exportableEntries)
    ? 'None of the cell tables can be exported with formatted column names.'
    : isEmpty(selectedEntries)
    ? 'Select at least one cell table.'
    : '';

  // `originals` holds the tables whose stored file is downloaded instead of a formatted one, with the reason their
  // formatting failed. A failed attempt offers those tables again as originals, keeping the others formatted.
  const startDownload = (downloadEntries: FormattedCellTableEntry[], originals: Map<string, string> = new Map()) => {
    if (!studyId || isEmpty(downloadEntries)) return;
    setHandoffMessage('');
    const buildOperation = (originalReasons: Map<string, string>): StudyExportOperation => {
      const failures = new Map(originalReasons);
      // A cached link that no longer works is asked for again once; the table keeps the mapping the user reviewed.
      const prepareEntry = async (entry: FormattedCellTableEntry, signal: AbortSignal) => {
        try {
          return await prepareFormattedCellTable({ studyId, entry, signal });
        } catch (error) {
          if (isRenameFailure(error)) failures.set(entry.file.id, error.message);
          if (!(error instanceof FormattedCellTableError) || error.code !== 'source_unavailable') throw error;
          const refreshed = await refreshCellTableEntryLinks({ cache: runCache.current, entry, signal });
          setLoadState((state) =>
            state.phase === 'ready'
              ? {
                  ...state,
                  entries: map(state.entries, (listed) => (listed.file.id === entry.file.id ? refreshed : listed)),
                }
              : state
          );
          try {
            return await prepareFormattedCellTable({ studyId, entry: { ...entry, file: refreshed.file }, signal });
          } catch (retryError) {
            if (isRenameFailure(retryError)) failures.set(entry.file.id, retryError.message);
            throw retryError;
          }
        }
      };
      return {
        title: 'Cell tables export',
        canCancel: true,
        closeOnSuccess: true,
        initialStage: 'Preparing the cell tables…',
        getFallback: () => {
          const failed = filter(downloadEntries, ({ file }) => failures.has(file.id));
          const hasNewFailure = some(failed, ({ file }) => !originalReasons.has(file.id));
          if (!hasNewFailure) return undefined;
          const titles = map(failed, ({ file }) => file.title).join(', ');
          const reasons = uniq(map(failed, ({ file }) => `${file.title}: ${failures.get(file.id)}`));
          return {
            label:
              downloadEntries.length === 1
                ? 'Download original'
                : `Download with ${size(failed)} original ${size(failed) === 1 ? 'file' : 'files'}`,
            message:
              downloadEntries.length === 1
                ? `The column names could not be formatted for ${titles}. Please contact Nucleai support and include the error details. You can still download the original file, with its stored column names.`
                : `The column names could not be formatted for ${titles}. Please contact Nucleai support and include the error details. You can still download the ZIP: the other tables stay formatted and ${
                    size(failed) === 1 ? 'this table is' : 'these tables are'
                  } included as the original file, marked "_original" in the file name.`,
            report: reportFor(reasons),
            operation: buildOperation(failures),
          };
        },
        run: async (signal, reportProgress) => {
          if (downloadEntries.length === 1) {
            const [entry] = downloadEntries;
            if (originalReasons.has(entry.file.id)) {
              await downloadOriginalFile(entry.file, signal);
              setHandoffMessage('The original file was handed to your browser. Check your downloads folder.');
              return;
            }
            const blob = await prepareEntry(entry, signal);
            const [fileName] = getZipEntryFileNames([{ ...entry.file, url: entry.file.urlForGetRequest }]);
            downloadBlob(blob, fileName);
            setHandoffMessage('The download was handed to your browser. Check your downloads folder.');
            return;
          }
          const files: FileToDownload[] = map(downloadEntries, (entry) =>
            originalReasons.has(entry.file.id)
              ? {
                  id: entry.file.id,
                  url: entry.file.urlForGetRequest,
                  title: entry.file.title,
                  fileName: getOriginalFileName(entry.file),
                  contentLength: entry.byteLength,
                }
              : {
                  id: entry.file.id,
                  url: entry.file.urlForGetRequest,
                  title: entry.file.title,
                  caseId: entry.file.caseId,
                  slideId: entry.file.slideId,
                  orchestrationId: entry.file.orchestrationId,
                  contentLength: entry.byteLength,
                  prepare: (prepareSignal: AbortSignal) => prepareEntry(entry, prepareSignal),
                }
          );
          try {
            await downloadFilesAsZip({
              files,
              archiveName: `cell_tables_study_${studyId}_formatted.zip`,
              signal,
              onProgress: ({ phase, completedFiles, totalFiles }) =>
                reportProgress({
                  stage:
                    phase === 'fetching'
                      ? `Preparing the cell tables… ${completedFiles} of ${totalFiles}`
                      : phase === 'archiving'
                      ? 'Creating the ZIP archive…'
                      : 'Handing the ZIP to your browser…',
                  current: completedFiles,
                  total: totalFiles,
                }),
            });
          } catch (error) {
            if (error instanceof ZipDownloadError && error.failureReasons.length) {
              throw new Error(`${error.message}. ${uniq(error.failureReasons).join(' ')}`);
            }
            throw error;
          }
          setHandoffMessage('The ZIP was handed to your browser. Check your downloads folder.');
        },
      };
    };
    void startExport(buildOperation(originals));
  };

  return (
    <>
      <Dialog
        open
        fullWidth
        maxWidth="md"
        onClose={close}
        onClick={(event) => event.stopPropagation()}
        aria-labelledby="workflow-cell-tables-export-title"
      >
        <DialogChromeHeader
          titleId="workflow-cell-tables-export-title"
          title="Export cell tables"
          onClose={close}
          closeButtonAriaLabel="Close export"
        />
        {isUnavailableForUser || !study ? (
          <>
            <DialogContent dividers>
              <Typography variant="body2">{availability.note}</Typography>
            </DialogContent>
            <DialogChromeFooter>
              <Button color="inherit" onClick={close}>
                Close
              </Button>
            </DialogChromeFooter>
          </>
        ) : (
          <ExportFormShell
            study={study}
            optionsTitle="Cell tables"
            downloadOnly
            scope={
              <ExportScopeFields>
                <ExportScopeField
                  id="workflow-cell-tables-cases"
                  label="Cases"
                  options={[{ value: 'whole', label: getScopeOptionLabels(false).whole }]}
                  value="whole"
                  onChange={noop}
                />
                {source.canViewUnpublishedResults && study ? (
                  <WorkflowResultsSourceSelect
                    kind="cellTables"
                    study={study}
                    selection={source.selection}
                    onChange={source.select}
                    runs={source.runs}
                    staleRunIds={source.staleRunIds}
                    isLoadingRuns={source.isLoadingRunOptions}
                    runNames={source.runNames}
                    hasRunsError={source.hasRunsError}
                    runsError={source.runsError}
                  />
                ) : (
                  <ExportScopeValue label="Results">Published results</ExportScopeValue>
                )}
              </ExportScopeFields>
            }
            options={
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Final cell tables, downloaded as Parquet with panel names in the column headings. The stored files are
                  not changed.
                </Typography>
                {isAvailable && loadState.phase === 'loading' && (
                  <Loader inline message={loadState.message} ariaLabel="Loading cell tables" />
                )}
                {isAvailable && loadState.phase === 'ready' && loadState.loadingMessage && (
                  <Loader inline message={loadState.loadingMessage} ariaLabel="Loading cell tables" />
                )}
                {isAvailable && loadState.phase === 'failed' && (
                  <ExportErrorAlert
                    title="The cell tables could not be loaded"
                    errors={loadState.errors}
                    report={reportFor(loadState.errors)}
                  />
                )}
                {isAvailable && loadState.phase === 'ready' && !isEmpty(loadState.failures) && (
                  <ExportErrorAlert
                    title={describeFailedRuns(size(loadState.failures))}
                    errors={map(loadState.failures, describeRunFailure)}
                    report={reportFor(map(loadState.failures, describeRunFailure))}
                  />
                )}
                {isAvailable && loadState.phase === 'ready' && !isEmpty(unreadableEntries) && (
                  <ExportErrorAlert
                    title={`${countLabel(size(unreadableEntries), 'cell table')} could not be read`}
                    errors={uniq(map(unreadableEntries, ({ file, reasons }) => `${file.title}: ${reasons.join(' ')}`))}
                    report={reportFor(
                      map(unreadableEntries, ({ file, reasons }) => `${file.title}: ${reasons.join(' ')}`)
                    )}
                  />
                )}
                {isAvailable && loadState.phase === 'ready' && !isEmpty(loadState.emptyRunIds) && (
                  <Alert severity="info">
                    No final cell tables in {loadState.emptyRunIds.length === 1 ? 'run' : 'runs'}{' '}
                    {loadState.emptyRunIds.join(', ')}.
                  </Alert>
                )}
                {isAvailable && loadState.phase === 'ready' && loadState.collapsedDuplicates > 0 && (
                  <Alert severity="info">
                    {countLabel(loadState.collapsedDuplicates, 'duplicate cell table')} sharing a file with another
                    listed table {loadState.collapsedDuplicates === 1 ? 'was' : 'were'} listed once.
                  </Alert>
                )}
                {isAvailable && loadState.phase === 'ready' && !isEmpty(entries) && (
                  <>
                    {size(entries) > 1 && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                          size="small"
                          label="Search cell tables"
                          value={search}
                          onChange={(event) => {
                            setSearch(event.target.value);
                            setVisibleCount(VISIBLE_FILES_STEP);
                          }}
                          sx={{ flex: 1 }}
                        />
                        <Button
                          size="small"
                          onClick={() => setSelectedIds(new Set(map(exportableEntries, (entry) => entry.file.id)))}
                        >
                          Select all
                        </Button>
                        <Button size="small" onClick={() => setSelectedIds(new Set())}>
                          Select none
                        </Button>
                      </Stack>
                    )}
                    <Typography variant="body2" color="text.secondary" role="status">
                      {selectedEntries.length} of {size(exportableEntries)} exportable cell tables selected
                      {unavailableCount > 0 ? `; ${unavailableCount} cannot be exported with formatted names` : ''}
                      {size(unreadableEntries) > 0
                        ? `; ${size(unreadableEntries)} could not be read because of an error`
                        : ''}
                      .
                    </Typography>
                    <Box role="group" aria-label="Cell tables">
                      {map(visibleGroups, (group, groupIndex) => (
                        <Box key={group.key}>
                          <CellTableGroupHeader group={group} tableCount={group.tableCount} groupIndex={groupIndex} />
                          <List dense disablePadding aria-label={`Cell tables, group ${groupIndex + 1}`}>
                            {map(group.entries, (entry) => (
                              <ListItem key={entry.file.id} disableGutters alignItems="flex-start">
                                <FormControlLabel
                                  sx={{ alignItems: 'flex-start', flex: 1, minWidth: 0, m: 0 }}
                                  control={
                                    <Checkbox
                                      checked={entry.status === 'ready' && effectiveSelection.has(entry.file.id)}
                                      disabled={entry.status !== 'ready'}
                                      onChange={() => toggle(entry.file.id)}
                                    />
                                  }
                                  label={
                                    <ListItemText
                                      primary={entry.file.title}
                                      secondary={
                                        <>
                                          {hasSeveralRuns && entry.file.orchestrationId
                                            ? `Run ${entry.file.orchestrationId}${entry.byteLength ? ', ' : ''}`
                                            : null}
                                          {entry.byteLength ? formatBytes(entry.byteLength) : null}
                                          {entry.status !== 'ready' &&
                                            map(entry.reasons, (reason) => (
                                              <Typography
                                                key={reason}
                                                component="span"
                                                variant="caption"
                                                color="error"
                                                sx={{ display: 'block' }}
                                              >
                                                {reason}
                                              </Typography>
                                            ))}
                                        </>
                                      }
                                      secondaryTypographyProps={{ component: 'div' }}
                                    />
                                  }
                                />
                                <Button
                                  size="small"
                                  variant="text"
                                  color="inherit"
                                  sx={{ flexShrink: 0 }}
                                  aria-label={`Download ${entry.file.title}${
                                    entry.file.orchestrationId ? ` from run ${entry.file.orchestrationId}` : ''
                                  }`}
                                  disabled={entry.status !== 'ready' || isExportRunning}
                                  onClick={() => startDownload([entry])}
                                >
                                  Download
                                </Button>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      ))}
                    </Box>
                    {size(matchingEntries) > visibleCount && (
                      <Button size="small" onClick={() => setVisibleCount((count) => count + VISIBLE_FILES_STEP)}>
                        Show more ({size(matchingEntries) - visibleCount} remaining)
                      </Button>
                    )}
                  </>
                )}
                {isAvailable && loadState.phase === 'ready' && !loadState.loadingMessage && isEmpty(entries) && (
                  <Typography variant="body2">
                    There are no final cell tables for this study and result source.
                  </Typography>
                )}
                {handoffMessage && <Alert severity="success">{handoffMessage}</Alert>}
              </Stack>
            }
            primaryLabel="Download"
            primaryDisabled={Boolean(guidance) || isExportRunning}
            primaryGuidance={guidance}
            onPrimary={() => startDownload(selectedEntries)}
            onBack={close}
            backLabel="Close"
          />
        )}
      </Dialog>
      {exportProgressDialog}
    </>
  );
};
